
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotificationDispatcher } from '@/hooks/useNotificationDispatcher';
import { EmailTemplateType } from '@/utils/email-templates';
import { transformCardForDatabase } from '@/types/insight';

type NotificationType = EmailTemplateType;

export const useCardOperations = (props?: {
  cards?: any[];
  setCards?: React.Dispatch<React.SetStateAction<any[]>>;
  setDialogOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { dispatch } = useNotificationDispatcher();

  const updateCard = useCallback(async (cardId, cardData) => {
    setIsProcessing(true);
    try {
      // Transform the card data for the database
      const dbCard = transformCardForDatabase(cardData);
      
      // Update the card in the database
      const { error } = await supabase
        .from('insight_cards')
        .update(dbCard)
        .eq('id', cardId);
        
      if (error) throw error;
      
      // If props are provided, update the cards list
      if (props?.cards && props?.setCards) {
        props.setCards(props.cards.map(card => 
          card.id === cardId ? cardData : card
        ));
      }
      
      return true;
    } catch (error) {
      console.error('Failed to update card:', error);
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [props]);

  const deleteCard = useCallback(async (cardId) => {
    setIsProcessing(true);
    try {
      // Delete the card from the database
      const { error } = await supabase
        .from('insight_cards')
        .delete()
        .eq('id', cardId);
        
      if (error) throw error;
      
      // If props are provided, update the cards list
      if (props?.cards && props?.setCards) {
        props.setCards(props.cards.filter(card => card.id !== cardId));
      }
      
      return true;
    } catch (error) {
      console.error('Failed to delete card:', error);
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [props]);

  const saveCard = useCallback(async (cardData, isEditing) => {
    setIsProcessing(true);
    try {
      // Transform the card data for the database
      const dbCard = transformCardForDatabase(cardData);
      
      if (isEditing) {
        // Update existing card
        await updateCard(cardData.id, cardData);
      } else {
        // Create new card
        const { data, error } = await supabase
          .from('insight_cards')
          .insert(dbCard)
          .select('*')
          .single();
          
        if (error) throw error;
        
        // Insert insights if present
        if (cardData.insights && cardData.insights.length > 0) {
          const insightRecords = cardData.insights.map(insight => ({
            card_id: dbCard.id,
            insight_text: insight
          }));
          
          const { error: insightsError } = await supabase
            .from('card_insights')
            .insert(insightRecords);
            
          if (insightsError) {
            console.error('Failed to insert insights:', insightsError);
          }
        }
        
        // If props are provided, update the cards list
        if (props?.cards && props?.setCards) {
          props.setCards([data, ...props.cards]);
        }
      }
      
      // Close dialog if needed
      if (props?.setDialogOpen) {
        props.setDialogOpen(false);
      }
      
      return true;
    } catch (error) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} card:`, error);
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [updateCard, props]);

  const notifyOfInsightUpdate = useCallback(async (cardId, updates) => {
    try {
      // Use the correct template key that matches with EmailTemplateType
      const notificationType: NotificationType = 'insightUpdated';
      
      // Implementation details...
      
      return true;
    } catch (error) {
      console.error('Error sending insight update notification:', error);
      return false;
    }
  }, [dispatch]);

  return {
    updateCard,
    deleteCard,
    saveCard,
    notifyOfInsightUpdate,
    isProcessing
  };
};
